import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const CONFIG = {
  loopDuration: 45,
  loopDirection: 1,
  perspective: 1200,
  maxTilt: 35,
  baseHeight: 460,
  gap: 16,
  introSpeed: 3,
  introDuration: 4,
  introFade: 0.6,
  introStagger: 0.12,
  pointDuration: 0.7,
  pointStagger: 0.25,
  pointYFrom: 40,
  pointScaleFrom: 0.92,
}

function init() {
  const section = document.querySelector('.js-strength')
  if (!section) return

  const photos = gsap.utils.toArray('.js-strength-photo')
  const points = gsap.utils.toArray('.js-strength-point')
  const heading = document.querySelector('.js-strength-heading')

  const orbit = { angle: 0 }
  const alphas = photos.map(() => ({ v: 0 }))
  let loopTween = null

  function renderOrbit() {
    const dir = -CONFIG.loopDirection
    const P = CONFIG.perspective
    const W2 = 150
    const T = (CONFIG.maxTilt * Math.PI) / 180

    const st = photos
      .map((p, i) => {
        let u = (orbit.angle * dir + i / photos.length) % 1
        if (u > 0.5) u -= 1
        if (u < -0.5) u += 1
        const phi = -(u / 0.4) * T
        const sL = P / (P - W2 * Math.sin(phi))
        const sR = P / (P + W2 * Math.sin(phi))
        return { p, i, u, phi, sL, sR, wc: W2 * Math.cos(phi) }
      })
      .sort((a, b) => a.u - b.u)

    let anchor = 0
    st.forEach((s, k) => {
      if (Math.abs(s.u) < Math.abs(st[anchor].u)) anchor = k
    })
    st[anchor].h = CONFIG.baseHeight
    for (let k = anchor + 1; k < st.length; k++)
      st[k].h = (st[k - 1].h * st[k - 1].sR) / st[k].sL
    for (let k = anchor - 1; k >= 0; k--)
      st[k].h = (st[k + 1].h * st[k + 1].sL) / st[k].sR

    const G = CONFIG.gap
    const screenR = (s) => (s.x + s.wc) * s.sR
    const screenL = (s) => (s.x - s.wc) * s.sL
    let b = st.findIndex((s) => s.u >= 0)
    b = Math.min(Math.max(b, 1), st.length - 1)
    const mid = ((st[b - 1].u + st[b].u) / 2) * ((W2 + G / 2) / 0.1)
    st[b].x = (mid + G / 2) / st[b].sL + st[b].wc
    st[b - 1].x = (mid - G / 2) / st[b - 1].sR - st[b - 1].wc
    for (let k = b + 1; k < st.length; k++)
      st[k].x = (screenR(st[k - 1]) + G) / st[k].sL + st[k].wc
    for (let k = b - 2; k >= 0; k--)
      st[k].x = (screenL(st[k + 1]) - G) / st[k].sR - st[k].wc

    st.forEach((s) => {
      gsap.set(s.p, {
        x: s.x,
        y: -(s.h - CONFIG.baseHeight) / 2,
        height: s.h,
        rotationY: (s.phi * 180) / Math.PI,
        autoAlpha: alphas[s.i].v,
      })
    })
  }

  function startLoop() {
    loopTween = gsap.to(orbit, {
      angle: `+=${CONFIG.loopDirection}`,
      duration: CONFIG.loopDuration,
      ease: 'none',
      repeat: -1,
      onUpdate: renderOrbit,
    })
  }

  function playAnimation() {
    if (loopTween) loopTween.kill()
    gsap.killTweensOf(orbit)
    alphas.forEach((a) => gsap.killTweensOf(a))

    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

    orbit.angle = 0
    alphas.forEach((a) => (a.v = 0))
    renderOrbit()
    gsap.set(points, {
      autoAlpha: 0,
      y: CONFIG.pointYFrom,
      scale: CONFIG.pointScaleFrom,
      transformOrigin: '50% 50%',
    })
    gsap.set(heading, { opacity: 0, y: 30 })

    tl.to(heading, { opacity: 1, y: 0, duration: 0.8 })

    startLoop()
    loopTween.timeScale(CONFIG.introSpeed)
    gsap.to(loopTween, {
      timeScale: 1,
      duration: CONFIG.introDuration,
      ease: 'power2.out',
    })

    const dirIn = -CONFIG.loopDirection
    const order = photos
      .map((p, i) => {
        let u = (orbit.angle * dirIn + i / photos.length) % 1
        if (u > 0.5) u -= 1
        if (u < -0.5) u += 1
        return { i, u }
      })
      .sort((a, b) => a.u - b.u)
    tl.addLabel('photosIn', '-=0.3')
    order.forEach((o, k) => {
      tl.to(
        alphas[o.i],
        {
          v: 1,
          duration: CONFIG.introFade,
          onUpdate: renderOrbit,
        },
        `photosIn+=${k * CONFIG.introStagger}`
      )
    })

    tl.to(
      points,
      {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: CONFIG.pointDuration,
        stagger: CONFIG.pointStagger,
        ease: 'power2.out',
      },
      '-=0.7'
    )

    return tl
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    alphas.forEach((a) => (a.v = 1))
    renderOrbit()
    gsap.set(heading, { opacity: 1, y: 0 })
    gsap.set(points, { autoAlpha: 1, y: 0, scale: 1 })
  } else {
    ScrollTrigger.create({
      trigger: section,
      start: 'top 80%',
      once: true,
      onEnter: playAnimation,
    })
  }
}

export default {
  init() {
    init()
  },
}
